'use client';
import React, { useState } from 'react';
import { Search, MapPin, ArrowRight, ChevronRight, CalendarCheck, Clock, Users, Ticket } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import HomeButton from '@/components/HomeButton';

interface Event {
  id: string;
  title: string;
  organizer: string;
  category: string;
  date: string;
  time: string;
  location: string;
  city: string;
  price: string;
  attendees: number;
  isFree: boolean;
  description: string;
}

const EVENTS: Event[] = [
  { id: '1', title: 'Nairobi Tech Summit 2026', organizer: 'TechKenya', category: 'Technology', date: 'Aug 15, 2026', time: '9:00 AM', location: 'KICC, Nairobi', city: 'Nairobi', price: 'KES 2,500', attendees: 1200, isFree: false, description: 'Kenya\'s biggest tech conference. Speakers from Google, Safaricom, and top startups.' },
  { id: '2', title: 'Blankets & Wine Nairobi', organizer: 'Blankets & Wine', category: 'Music', date: 'Aug 22, 2026', time: '2:00 PM', location: 'Ngong Racecourse', city: 'Nairobi', price: 'KES 1,500', attendees: 3000, isFree: false, description: 'Outdoor music festival with top Kenyan and African artists. Food, wine, and good vibes.' },
  { id: '3', title: 'Mombasa Food Festival', organizer: 'Coast Events', category: 'Food', date: 'Sep 5, 2026', time: '11:00 AM', location: 'Mama Ngina Waterfront', city: 'Mombasa', price: 'Free', attendees: 5000, isFree: true, description: 'Celebrate coastal cuisine with 50+ food vendors, cooking demos, and live entertainment.' },
  { id: '4', title: 'Kenya Startup Pitch Night', organizer: 'iHub Nairobi', category: 'Business', date: 'Aug 28, 2026', time: '6:00 PM', location: 'iHub, Nairobi', city: 'Nairobi', price: 'Free', attendees: 200, isFree: true, description: 'Watch 10 startups pitch to investors. Networking and drinks after the event.' },
  { id: '5', title: 'Kisumu Marathon 2026', organizer: 'Athletics Kenya', category: 'Sports', date: 'Sep 12, 2026', time: '6:00 AM', location: 'Kisumu City Centre', city: 'Kisumu', price: 'KES 500', attendees: 2000, isFree: false, description: '5K, 10K, and 21K categories. Open to all fitness levels. Medals and prizes.' },
  { id: '6', title: 'Afro Fashion Week Nairobi', organizer: 'Fashion Kenya', category: 'Fashion', date: 'Oct 3, 2026', time: '5:00 PM', location: 'Radisson Blu, Nairobi', city: 'Nairobi', price: 'KES 3,000', attendees: 800, isFree: false, description: 'Showcasing top African designers. Runway shows, exhibitions, and networking.' },
];

const CATEGORIES = ['All', 'Technology', 'Music', 'Food', 'Business', 'Sports', 'Fashion'];
const CITIES = ['All Cities', 'Nairobi', 'Mombasa', 'Kisumu'];

export default function EventsPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [city, setCity] = useState('All Cities');
  const [freeOnly, setFreeOnly] = useState(false);

  const filtered = EVENTS.filter(e => {
    const matchSearch = !search || e.title.toLowerCase().includes(search.toLowerCase()) || e.organizer.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || e.category === category;
    const matchCity = city === 'All Cities' || e.city === city;
    const matchFree = !freeOnly || e.isFree;
    return matchSearch && matchCat && matchCity && matchFree;
  });

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1a0a1a 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #E040FB 0%, transparent 50%), radial-gradient(circle at 70% 30%, #7C4DFF 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-between mb-4">
              <HomeButton />
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600"
              style={{ background: 'rgba(224,64,251,0.15)', color: '#E040FB', border: '1px solid rgba(224,64,251,0.3)' }}>
                <CalendarCheck size={12} /> Events Across Kenya
              </div>
              <Link href="/create-listing?type=event" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-700"
                style={{ background: '#E040FB', color: '#fff' }}>
                + Create Event
              </Link>
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Discover <span style={{ color: '#E040FB' }}>Events</span> Near You
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Concerts, tech summits, food festivals, sports events, and more. Buy tickets instantly.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search events…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: category === c ? 'rgba(224,64,251,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#E040FB' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(224,64,251,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            ))}
            <div className="flex-shrink-0 w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {CITIES.map(ci => (
              <button key={ci} onClick={() => setCity(ci)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: city === ci ? 'rgba(124,77,255,0.15)' : 'rgba(255,255,255,0.04)', color: city === ci ? '#7C4DFF' : 'rgba(255,255,255,0.55)', border: `1px solid ${city === ci ? 'rgba(124,77,255,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {ci}
              </button>
            ))}
            <button onClick={() => setFreeOnly(!freeOnly)}
              className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
              style={{ background: freeOnly ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: freeOnly ? '#00C853' : 'rgba(255,255,255,0.55)', border: `1px solid ${freeOnly ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
              Free Only
            </button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#E040FB', fontWeight: 700 }}>{filtered.length}</span> events found
            </span>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#E040FB', color: '#fff' }}>
              Create Event <ArrowRight size={12} />
            </Link>
          </div>

          <div className="flex flex-col gap-3">
            {filtered.map(event => (
              <div key={event.id} className="p-4 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 text-center"
                    style={{ background: 'rgba(224,64,251,0.12)', border: '1px solid rgba(224,64,251,0.2)' }}>
                    <span className="text-[10px] font-700" style={{ color: '#E040FB' }}>{event.date.split(' ')[0]}</span>
                    <span className="text-lg font-800 text-white leading-tight">{event.date.split(' ')[1].replace(',', '')}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="text-sm font-700 text-white">{event.title}</h3>
                        <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{event.organizer}</p>
                      </div>
                      <span className="text-sm font-800 flex-shrink-0" style={{ color: event.isFree ? '#00C853' : '#E040FB' }}>
                        {event.price}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <div className="flex items-center gap-1">
                        <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{event.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{event.time}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{event.attendees.toLocaleString()} attending</span>
                      </div>
                    </div>
                    <p className="text-xs mt-2 line-clamp-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{event.description}</p>
                    <div className="flex items-center justify-end mt-3">
                      <Link href="/login"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700"
                        style={{ background: 'rgba(224,64,251,0.15)', color: '#E040FB', border: '1px solid rgba(224,64,251,0.25)' }}>
                        <Ticket size={11} /> Get Tickets <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(224,64,251,0.1), rgba(124,77,255,0.1))', border: '1px solid rgba(224,64,251,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">Create & Promote Your Event</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Reach thousands of event-goers across Kenya. Sell tickets online.</p>
            <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#E040FB', color: '#fff' }}>
              <CalendarCheck size={16} /> Create Event
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
